import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import type { Invitation, OnboardingDraft, OnboardingError } from '../domain/onboarding.model';
import { initialState } from './onboarding.state';

/**
 * Onboarding slice. Reducers are pure state transitions only; the I/O (the
 * invitation check and the accept call) lives in the saga, which dispatches
 * these `invitationValidation*` / `submit*` transitions as it progresses. This
 * is what lets the reducers be unit-tested as `(state, action) => state` with
 * zero mocks.
 */
const slice = createSlice({
  name: 'onboarding',
  initialState,
  reducers: {
    /** A single text field changed; clears that field's validation error. */
    fieldChanged: (
      state,
      action: PayloadAction<{ field: keyof OnboardingDraft; value: string }>,
    ) => {
      const { field, value } = action.payload;
      state.draft[field] = value;
      delete state.validationErrors[field];
    },
    // ── Invitation-check lifecycle (dispatched by the saga on mount) ──
    invitationValidationStarted: (state) => {
      state.invitationStatus = 'validating';
      state.invitationError = null;
    },
    invitationValidationSucceeded: (state, action: PayloadAction<Invitation>) => {
      state.invitationStatus = 'valid';
      state.invitation = action.payload;
    },
    invitationValidationFailed: (state, action: PayloadAction<string>) => {
      state.invitationStatus = 'invalid';
      state.invitationError = action.payload;
    },
    // ── Submit lifecycle (dispatched by the saga) ──
    submitStarted: (state) => {
      state.status = 'submitting';
      state.error = null;
      state.validationErrors = {};
    },
    submitSucceeded: (state) => {
      state.status = 'succeeded';
    },
    submitFailed: (state, action: PayloadAction<OnboardingError>) => {
      state.status = 'failed';
      state.error = action.payload;
      if (action.payload.kind === 'validation') {
        state.validationErrors = action.payload.fields;
      }
    },
    /** Reset to the pristine slice (on unmount). */
    resetForm: () => initialState,
  },
});

export const onboardingActions = slice.actions;
export const onboardingReducer = slice.reducer;
